import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { RootState } from "@/store/type";
import { ROLE } from "@/types";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { isUndefined } from "lodash";
import { useSelector } from "react-redux";

interface Product {
  itemId: string;
  item: string;
  deliveryMethod: string;
  quantity: number;
  price: number;
  discount: number;
  isCanceled: boolean;
  notice: string;
  image: string;
  attachment: string;
  companyId: string;
  company: {
    nameAr: string;
    CompanyNr: string;
  };
}
interface Order {
  id: string;
  title: string;
  deliverDate: string;
  city: string;

  address: string;
  products: Product[];
  receivedProducts: Product[];
  Sendedproducts: Product[];
  OrderNr: number;
  status: string;
  DeliveryMethod: string;
  paymentMethod: string;
}
export function SendedProducts({
  handleQuantitySChange,
  sendedItems,
  handleRemoveFromSended,
  isMobile,
  order,
}: {
  isMobile: boolean;
  handleQuantitySChange: (product: Product, quantity: number) => void;
  order: Order;
  handleRemoveFromSended: (product: Product) => void;
  sendedItems: Product[];
}) {
  const { locale } = useLocale();
  const authUser = useSelector((state: RootState) => state.auth.authUser);
  const userRole = isUndefined(authUser?.role) ? "" : authUser?.role;

  const { t } = useTranslate(locale);
  return (
    <div>
      <Card
        sx={{
          mb: 4,
          borderRadius: 3,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              mb: 3,
              color: "#1a1a1a",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            📤 {t("products.title")} {t("orders.actions.sent")}
          </Typography>
          <Divider sx={{ mb: 3 }} />

          {/* Mobile Sent Products View */}
          {isMobile ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {order.Sendedproducts.map(
                (product) =>
                  product.quantity > 0 && (
                    <Card
                      key={product.itemId}
                      sx={{
                        borderRadius: 2,
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                          {product.image && (
                            <img
                              src={product.image}
                              alt="item"
                              style={{
                                width: 60,
                                height: 60,
                                borderRadius: 8,
                                objectFit: "cover",
                                border: "1px solid #e0e0e0",
                              }}
                            />
                          )}
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" fontWeight={600}>
                              {product.item}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {t("orders.fields.supplier")}:{" "}
                              {product.company?.CompanyNr}
                            </Typography>
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 1,
                          }}
                        >
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {t("checkout.fields.quantity")}:
                            </Typography>
                            <TextField
                              type="number"
                              value={product.quantity}
                              onChange={(e) =>
                                handleQuantitySChange(
                                  product,
                                  Number(e.target.value)
                                )
                              }
                              sx={{ width: "100%" }}
                              inputProps={{ min: 0 }}
                            />
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  )
              )}
              {sendedItems.map((item) => (
                <Card
                  key={item.itemId}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                      {item.image && (
                        <img
                          src={item.image}
                          alt="item"
                          style={{
                            width: 60,
                            height: 60,
                            borderRadius: 8,
                            objectFit: "cover",
                            border: "1px solid #e0e0e0",
                          }}
                        />
                      )}
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" fontWeight={600}>
                          {item.item}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t("orders.fields.supplier")}:{" "}
                          {item.company?.CompanyNr}
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 1,
                      }}
                    >
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {t("checkout.fields.quantity")}:
                        </Typography>
                        <TextField
                          type="number"
                          value={item.quantity}
                          onChange={(e) =>
                            handleQuantitySChange(item, Number(e.target.value))
                          }
                          sx={{ width: "100%" }}
                          inputProps={{ min: 0 }}
                        />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            /* Desktop Sent Products Table */
            <TableContainer sx={{ borderRadius: 2, overflow: "hidden" }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                    <TableCell sx={{ border: "none" }}></TableCell>
                    <TableCell sx={{ border: "none", fontWeight: 700 }}>
                      {t("products.name")}
                    </TableCell>
                    <TableCell sx={{ border: "none", fontWeight: 700 }}>
                      {t("orders.fields.supplier")}
                    </TableCell>
                    <TableCell sx={{ border: "none", fontWeight: 700 }}>
                      {t("checkout.fields.quantity")}
                    </TableCell>
                    {order.status !== "Sending" &&
                      order.status !== "Finished" && (
                        <TableCell sx={{ border: "none", fontWeight: 700 }}>
                          {t("general.actions")}
                        </TableCell>
                      )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {userRole === ROLE.SELLER &&
                    order.Sendedproducts.map(
                      (product) =>
                        product.quantity > 0 && (
                          <TableRow
                            key={product.itemId}
                            sx={{
                              "&:hover": { backgroundColor: "#f8f9fa" },
                              "&:last-child td": { border: 0 },
                            }}
                          >
                            <TableCell sx={{ border: "none" }}>
                              {product.image ? (
                                <img
                                  src={product.image}
                                  alt="item"
                                  style={{
                                    width: 60,
                                    height: 60,
                                    borderRadius: 8,
                                    objectFit: "cover",
                                    border: "1px solid #e0e0e0",
                                  }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    width: 60,
                                    height: 60,
                                    backgroundColor: "#f5f5f5",
                                    borderRadius: 8,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    —
                                  </Typography>
                                </Box>
                              )}
                            </TableCell>
                            <TableCell sx={{ border: "none", fontWeight: 600 }}>
                              {product.item}
                            </TableCell>
                            <TableCell sx={{ border: "none" }}>
                              {product.company?.CompanyNr}
                            </TableCell>
                            <TableCell sx={{ border: "none" }}>
                              {product.quantity}
                            </TableCell>
                            {order.status !== "Sending" &&
                              order.status !== "Finished" && (
                                <TableCell sx={{ border: "none" }}>
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    onClick={() =>
                                      handleRemoveFromSended(product)
                                    }
                                    sx={{
                                      borderColor: "#f44336",
                                      color: "#f44336",
                                      "&:hover": {
                                        borderColor: "#d32f2f",
                                        backgroundColor: "#ffebee",
                                      },
                                    }}
                                  >
                                    {t("actions.delete")}
                                  </Button>
                                </TableCell>
                              )}
                          </TableRow>
                        )
                    )}
                  {sendedItems.map((item) => (
                    <TableRow
                      key={item.itemId}
                      sx={{
                        "&:hover": { backgroundColor: "#f8f9fa" },
                        "&:last-child td": { border: 0 },
                      }}
                    >
                      <TableCell sx={{ border: "none" }}>
                        {item.image ? (
                          <img
                            src={item.image}
                            alt="item"
                            style={{
                              width: 60,
                              height: 60,
                              borderRadius: 8,
                              objectFit: "cover",
                              border: "1px solid #e0e0e0",
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              backgroundColor: "#f5f5f5",
                              borderRadius: 8,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell sx={{ border: "none", fontWeight: 600 }}>
                        {item.item}
                      </TableCell>
                      <TableCell sx={{ border: "none" }}>
                        {item.company?.CompanyNr}
                      </TableCell>
                      <TableCell sx={{ border: "none" }}>
                        <TextField
                          type="number"
                          value={item.quantity}
                          onChange={(e) => {
                            handleQuantitySChange(
                              item,
                              Number(e.target.value) >
                                (order?.products?.find(
                                  (element) => element.itemId == item.itemId
                                )?.quantity || 0)
                                ? order?.products?.find(
                                    (element) => element.itemId == item.itemId
                                  )?.quantity || Number(e.target.value)
                                : Number(e.target.value)
                            );
                          }}
                          size="small"
                          sx={{ width: 80 }}
                          inputProps={{
                            min: 0,
                            max:
                              (order.products.find(
                                (element) => element.itemId == item.itemId
                              )?.quantity ?? 0) -
                              (order.Sendedproducts.find(
                                (element) => element.itemId == item.itemId
                              )?.quantity ?? 0),
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ border: "none" }}>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => handleRemoveFromSended(item)}
                          sx={{
                            borderColor: "#f44336",
                            color: "#f44336",
                            "&:hover": {
                              borderColor: "#d32f2f",
                              backgroundColor: "#ffebee",
                            },
                          }}
                        >
                          {t("actions.delete")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
